import {
  Box,
  Button,
  FormHelperText,
  Paper,
  TextField,
  FormControl,
  FormLabel,
  Stack,
  Typography,
} from '@mui/material';
import { get } from 'lodash';
import { ReactElement } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';

import { AdditionalButton } from '@app/components/Shared/Button/AdditionalButton';
import { ImageUpload } from '@app/components/Shared/Inputs/ImageUpload';
import { getResizeImageURL } from '@app/utils/fileUpload';

export function FloorPlanFormSection(): ReactElement {
  const {
    clearErrors,
    control,
    formState: { errors },
    setError,
    setValue,
    watch,
  } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'floorPlans',
  });

  return (
    <Stack spacing={3}>
      <Typography variant="h5">間取り、モデルハウス情報</Typography>
      {fields.map((field, index) => (
        <Stack key={field.id} spacing={1.5}>
          <Stack direction="row" spacing={1.5} alignItems="flexEnd">
            <Typography variant="h6">point{index + 1}</Typography>
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              onClick={() => remove(index)}
              sx={{ height: 32 }}
            >
              <Typography variant="body3">クリア</Typography>
            </Button>
          </Stack>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Stack direction="row" spacing={3} useFlexGap>
              <Box flex={1}>
                <ImageUpload
                  fileInfo={watch(`floorPlans.${index}.url`)}
                  onFileUpload={async (urls) => {
                    const resizeUrl = await getResizeImageURL(
                      urls[0],
                      undefined,
                      undefined,
                      3 / 2
                    );
                    clearErrors(`floorPlans.${index}`);
                    setValue(`floorPlans.${index}.imageId`, '');
                    setValue(`floorPlans.${index}.url`, resizeUrl);
                  }}
                  onError={(e) =>
                    setError(`floorPlans.${index}`, { message: e.message })
                  }
                />
                {Array.from(
                  new Set([get(errors, `floorPlans.${index}.message`, '')])
                )
                  .filter((m) => !!m)
                  .map((message, index) => (
                    <FormHelperText key={index} error>
                      {message as string}
                    </FormHelperText>
                  ))}
              </Box>
              <Stack spacing={1.5} flex={1}>
                <FormControl fullWidth>
                  <FormLabel focused={false}>説明タイトル</FormLabel>
                  <Controller
                    name={`floorPlans.${index}.title`}
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </FormControl>
                <FormControl fullWidth>
                  <FormLabel focused={false}>説明本文</FormLabel>
                  <Controller
                    name={`floorPlans.${index}.description`}
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        error={!!error}
                        helperText={error?.message}
                        maxRows={4}
                        multiline
                      />
                    )}
                  />
                </FormControl>
              </Stack>
            </Stack>
          </Paper>
        </Stack>
      ))}
      <AdditionalButton
        label="間取りの追加"
        disabled={fields.length >= 6}
        fullWidth
        onClick={() =>
          append({
            description: '',
            id: '',
            imageId: '',
            title: '',
            url: '',
          })
        }
      />
    </Stack>
  );
}
